<script setup lang="ts">
import {
    getAuth,
    createUserWithEmailAndPassword,
    validatePassword,
    PasswordValidationStatus,
    fetchSignInMethodsForEmail
} from "firebase/auth";
import { Ref, ref } from "vue";
import { useRouter } from "vue-router";
import AccountAction from "@/components/AccountAction.vue";
import AccountPasswordValidation from "@/components/AccountPasswordValidation.vue";
import AppInputPhone from "@/components/AppInputPhone.vue";
import AppInputText from "@/components/AppInputText.vue";
import AppButton from "@/components/AppButton.vue";
import AppLink from "@/components/AppLink.vue";
import AppSwitch from "@/components/AppSwitch.vue";
import AppCancel from "@/components/AppCancel.vue";
import useFormValidation from "@/modules/useFormValidation";
import { useUserProfileStore } from "@/store/UserProfile";
import usePhone from "@/modules/usePhone";
import Config from "@/config/Config";

const auth = getAuth();
const router = useRouter();
const { setDisplayName, setPhoneNumber, syncProfileWithDb, setTermsOfService, setPrivacyPolicy } = useUserProfileStore();

interface Props {
    username: string;
    password: string;
}

const props = defineProps<Props>();

const emit = defineEmits(["cancel", "recoverPassword"]);

const {
    validateRequiredField,
    validateEmailField,
    errors,
    hasErrors
} = useFormValidation();
const { sanitizePhoneNumber, validatePhoneInput, validatePhoneHasValue } = usePhone(errors);

const verified = ref(false);
const username = ref(props.username);
const firstLastName = ref("");
const phoneNumber = ref("");
const newPassword = ref(props.password);
const validatePasswordResult: Ref<PasswordValidationStatus | null> = ref(null);
const inputMode = ref("password");
const message = ref("");
const acceptedTermsOfService = ref(false);
const acceptedPrivacyPolicy = ref(false);

const validateNameInput = () => validateRequiredField("name", "First & last name", firstLastName.value);
const validateEmailInput = () => validateEmailField("email", "Email", username.value);
const validateUserPassword = async () => validateRequiredField("password", "Password", newPassword.value);

const validateTermsOfServiceInput = () => {
    errors.set(
        "acceptedTermsOfService",
        acceptedTermsOfService.value ? "" : "Terms of Service must be accepted"
    );
};

const validatePrivacyPolicyInput = () => {
    errors.set(
        "acceptedPrivacyPolicy",
        acceptedPrivacyPolicy.value ? "" : "Privacy Policy must be accepted"
    );
};

const validatePasswordPolicy = async () => {
    validatePasswordResult.value = null;
    if (newPassword.value) {
        validatePasswordResult.value = await validatePassword(auth, newPassword.value);
    }
};

const validateAll = async () => {
    validateNameInput();
    validateEmailInput();
    validateUserPassword();
    validatePhoneHasValue(phoneNumber.value);
    validateTermsOfServiceInput();
    validatePrivacyPolicyInput();
    await validatePasswordPolicy();
};

const handleIconClick = () => {
    inputMode.value = inputMode.value === "password" ? "text" : "password";
};

const handleExistingAccount = async () => {
    await fetchSignInMethodsForEmail(auth, username.value)
        .then((signInMethods) => {
            if (signInMethods.includes("password")) {
                message.value = "The email address is already in use by another account.";
            } else {
                emit("recoverPassword", {
                    username: username.value,
                    message: "We've identified a matching account, but it needs a password reset."
                });
            }
        })
        .catch(() => {
            message.value = "Error creating account. Please try again.";
        });
};

const createUser = async () => {
    try {
        await validateAll();

        if (hasErrors.value || !validatePasswordResult.value?.isValid) {
            return;
        }

        const phone = sanitizePhoneNumber(phoneNumber.value);
        await createUserWithEmailAndPassword(auth, username.value, newPassword.value);
        setDisplayName(firstLastName.value);
        setPhoneNumber(phone as string);
        setTermsOfService(acceptedTermsOfService.value);
        setPrivacyPolicy(acceptedPrivacyPolicy.value);
        syncProfileWithDb();
        verified.value = true;
        router.push({ name: "VerifyConfirmationCode" });
    } catch (error) {
        if (error?.code === "auth/email-already-in-use") {
            handleExistingAccount();
        } else {
            message.value = "Error creating account. Please try again.";
        }
    }
};
</script>

<template>
    <AccountAction>
        <template #title>
            Create account
        </template>
        <template #default>
            <div class="flex flex-col gap-4">
                <p v-show="message" class="text-critical-text text-sm">
                    {{ message }}
                </p>

                <AppInputText id="sign-up-email" data-testId="sign-up-email" name="email" label="Email" type="email"
                    v-model="username" :required="true" @blur="validateEmailInput" @keyup="validateEmailInput" />

                <AppInputText id="sign-up-name" data-testId="sign-up-name" name="name" label="First & last name"
                    type="text" v-model="firstLastName" :required="true" @blur="validateNameInput"
                    @keyup="validateNameInput" />

                <AppInputPhone name="phone" v-model="phoneNumber" placeholder="Mobile Phone" label="Mobile phone"
                    @validate="validatePhoneInput" />

                <div class="flex flex-col gap-2">
                    <AppInputText id="sign-up-password" data-testId="sign-up-password" name="password" label="Password"
                        :type="inputMode" v-model="newPassword" :required="true" @blur="validateUserPassword"
                        @keyup="validateUserPassword" @update:modelValue="errors.set('password', '')">
                        <template #suffix>
                            <button type="button" @click="handleIconClick"
                                class="text-normal-standard hover:text-normal-emphasis">
                                <i :class="[
                                    'fas',
                                    inputMode === 'password' ? 'fa-eye' : 'fa-eye-slash'
                                ]"></i>
                            </button>
                        </template>
                    </AppInputText>

                    <AccountPasswordValidation v-if="validatePasswordResult"
                        :passwordValidationStatus="validatePasswordResult" />
                </div>

                <!-- Terms of Service and Privacy Policy switches -->
                <div class="flex flex-col gap-2 my-4">
                    <div class="flex items-center gap-2">
                        <AppSwitch v-model="acceptedTermsOfService" @update:modelValue="validateTermsOfServiceInput" />
                        <span class="text-sm mx-2">
                            I accept the
                            <a :href="Config.links.termsOfService" target="_blank"
                                class="text-action hover:text-action/90 underline">
                                Terms of Service
                            </a>
                        </span>
                    </div>
                    <p v-if="errors.get('acceptedTermsOfService')" class="text-critical-text text-sm mx-2">
                        {{ errors.get('acceptedTermsOfService') }}
                    </p>

                    <div class="flex items-center gap-2">
                        <AppSwitch v-model="acceptedPrivacyPolicy" @update:modelValue="validatePrivacyPolicyInput" />
                        <span class="text-sm mx-2">
                            I accept the
                            <a :href="Config.links.privacyPolicy" target="_blank"
                                class="text-action hover:text-action/90 underline">
                                Privacy Policy
                            </a>
                        </span>
                    </div>
                    <p v-if="errors.get('acceptedPrivacyPolicy')" class="text-critical-text text-sm mx-2">
                        {{ errors.get('acceptedPrivacyPolicy') }}
                    </p>
                </div>
            </div>
        </template>
        <template #actions>
            <AppButton class="w-full" title="Create Account" @click="createUser"
                :disabled="!acceptedTermsOfService || !acceptedPrivacyPolicy" />
            <AppCancel class="mb-4" @click="$emit('cancel')" />
            <div class="flex flex-col gap-2">
                <p class="text-sm text-gray-600 italic text-center mt-2">By providing your phone number, you consent to
                    receive text
                    messages
                    from Aces Poker Run regarding your registered events. These messages are strictly for event-related
                    communication
                    and will not be used for marketing purposes or shared with third parties. Standard text messaging
                    rates
                    may
                    apply.
                </p>
                <p class="text-sm text-gray-600 italic text-center mt-2">
                    For more information or support, visit <a class="underline" href="https://www.acespokerrun.com"
                        target="_system">www.acespokerrun.com</a>. </p>
                <p class="text-sm text-gray-600 italic text-center mt-2"> To stop receiving messages, text "STOP" at any
                    time.
                    Please
                    note that opting out may result in missing important event notifications. </p>
            </div>
        </template>
    </AccountAction>
</template>
